import { Badge, Button } from '@artosera/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  useCampagnes,
  useListesNewsletter,
  type CampagneNewsletter,
} from '../../api/newsletter.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { BoutonDupliquer } from './BoutonDupliquer.js';
import { NouvelleCampagneDialog } from './NouvelleCampagneDialog.js';
import { formaterTaux, tauxSur } from './taux.js';
import styles from './NewsletterPage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';
const FILTRES = ['toutes', 'brouillon', 'programmee', 'envoyee', 'echouee'] as const;
type Filtre = (typeof FILTRES)[number];
export function CampagnesTab() {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const campagnes = useCampagnes(galerieSlug);
  const listes = useListesNewsletter(galerieSlug);
  const [nouvelle, setNouvelle] = useState(false);
  const [filtre, setFiltre] = useState<Filtre>('toutes');
  const toutes = campagnes.data?.campagnes ?? [];
  const visibles = toutes.filter((c) => filtre === 'toutes' || c.etat === filtre);
  const nombre = (f: Filtre) =>
    f === 'toutes' ? toutes.length : toutes.filter((c) => c.etat === f).length;
  const nomsListes = (c: CampagneNewsletter) =>
    c.listes.length
      ? c.listes.map((id) => listes.data?.listes.find((l) => l.id === id)?.nom ?? '…').join(' · ')
      : t('regie:newsletter.campagnes.aucuneListe');
  const date = (v: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(v));
  return (
    <section className={styles.section} data-ui="newsletter-campaigns">
      <Verrouillable
        action="newsletter:write"
        ouvert={galerieALaction(galerie, 'newsletter:write')}
      >
        <Button onClick={() => setNouvelle(true)}>
          {t('regie:newsletter.campagnes.nouvelle')}
        </Button>
      </Verrouillable>
      {campagnes.isPending ? <p>{t('commun:chargement')}</p> : null}
      {campagnes.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
      {campagnes.data && !toutes.length ? (
        <p className={styles.vide}>{t('regie:newsletter.campagnes.vide')}</p>
      ) : null}
      {toutes.length ? (
        <>
          <div
            className={styles.filtres}
            role="group"
            aria-label={t('regie:newsletter.campagnes.filtrer')}
          >
            {FILTRES.filter((f) => f === 'toutes' || nombre(f) > 0).map((f) => (
              <button
                type="button"
                key={f}
                className={styles.filtre}
                aria-pressed={filtre === f}
                onClick={() => setFiltre(f)}
              >
                {t(`regie:newsletter.campagnes.filtres.${f}`)} · {nombre(f)}
              </button>
            ))}
          </div>
          <div className={styles.tableau}>
            <table>
              <thead>
                <tr>
                  <th scope="col">{t('regie:newsletter.campagnes.colonnes.titre')}</th>
                  <th scope="col">{t('regie:newsletter.campagnes.colonnes.etat')}</th>
                  <th scope="col" className={styles.secondaire}>
                    {t('regie:newsletter.campagnes.colonnes.listes')}
                  </th>
                  <th scope="col">{t('regie:newsletter.campagnes.colonnes.envoyeeLe')}</th>
                  <th scope="col">{t('regie:newsletter.campagnes.colonnes.ouvertures')}</th>
                  <th scope="col" className={styles.secondaire}>
                    {t('regie:newsletter.campagnes.colonnes.clics')}
                  </th>
                  <th scope="col">
                    <span className={styles.masque}>
                      {t('regie:newsletter.campagnes.colonnes.actions')}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link to={`/${galerieSlug}/newsletter/campagnes/${c.id}`}>{c.titre}</Link>
                      <span className={styles.sousTitre}>{c.sujet}</span>
                    </td>
                    <td>
                      <Badge
                        tone={
                          c.etat === 'envoyee'
                            ? 'available'
                            : c.etat === 'echouee'
                              ? 'sold'
                              : 'neutral'
                        }
                      >
                        {t(`regie:newsletter.etats.${c.etat}`)}
                      </Badge>
                    </td>
                    <td className={styles.secondaire}>{nomsListes(c)}</td>
                    <td>{c.envoyeeLe ? date(c.envoyeeLe) : '—'}</td>
                    <td className={styles.chiffre}>
                      {formaterTaux(
                        c.statistiques
                          ? tauxSur(c.statistiques.ouvertures, c.statistiques.delivres)
                          : null,
                      )}
                    </td>
                    <td className={`${styles.chiffre} ${styles.secondaire}`}>
                      {formaterTaux(
                        c.statistiques
                          ? tauxSur(c.statistiques.clics, c.statistiques.delivres)
                          : null,
                      )}
                    </td>
                    <td>
                      <BoutonDupliquer campagne={c} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
      <NouvelleCampagneDialog open={nouvelle} onOpenChange={setNouvelle} />
    </section>
  );
}
